/**
 * Department Allocation Page Component
 *
 * Page for assigning users and providers to departments in a facility.
 *
 * @component
 */

import React, { useState, useEffect, useCallback } from "react";
import { Card, Skeleton, Typography, Empty, message, Tooltip } from "antd";
import { Box, Checkbox } from "@mui/material";
import { Search, Button } from "@voicecare.ai/ui-library";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { useTheme } from "../../ThemeContext";
import DepartmentAllocationService, {
  saveStaffAllocation,
  saveProviderAllocation,
  getDepartmentDetails,
} from "../../services/department-allocation-service";
import LogoLoader from "../common/loaders/gif-logo-loader";
import ThemedTooltip from "../common/ThemedTooltip";

const { Title, Text } = Typography;

const DepartmentAllocation = ({
  facilityData,
  onClose,
  onSave,
  loading: facilityLoading = false,
}) => {
  const { themeMode, currentTheme } = useTheme();
  const brandOrange = "#e63e2b";
  
  // Card height configuration
  const CARD_HEIGHT = "68vh";
  const LIST_MAX_HEIGHT = "50vh";
  const [searchParams, setSearchParams] = useSearchParams();

  // State management for allocation data
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [providers, setProviders] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);

  // Department-specific assignment data (from department endpoint)
  const [departmentStaffIds, setDepartmentStaffIds] = useState([]);
  const [departmentProviderIds, setDepartmentProviderIds] = useState([]);

  // Search states
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [providerSearchQuery, setProviderSearchQuery] = useState("");

  // Filter state for floating members (applies to both users and providers)
  const [showOnlyFloatingMembers, setShowOnlyFloatingMembers] = useState(false);

  // Selected users and providers for the current department
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedProviderIds, setSelectedProviderIds] = useState([]);

  // Loading states
  const [initialLoading, setInitialLoading] = useState(true);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [providersLoading, setProvidersLoading] = useState(false);

  // Track whether facility data has been loaded to prevent unnecessary reloads
  const [facilityDataLoaded, setFacilityDataLoaded] = useState(false);

  // Loading states for save buttons
  const [savingUsers, setSavingUsers] = useState(false);
  const [savingProviders, setSavingProviders] = useState(false);

  // Flag to prevent duplicate department details loading during initial load
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const facilityName = facilityData?.facilityName || "Unknown Facility";
  const facilityAddress = facilityData?.location
    ? `${facilityData.location.streetAddress}, ${facilityData.location.city}, ${facilityData.location.state}`
    : "No address available";

  /**
   * Updates URL with departmentId parameter
   */
  const updateDepartmentIdInURL = useCallback(
    (departmentId) => {
      if (departmentId) {
        setSearchParams((prevParams) => {
          const newParams = new URLSearchParams(prevParams);
          newParams.set("departmentId", departmentId);
          return newParams;
        });
      }
    },
    [setSearchParams]
  );

  /**
   * Loads department details to get assigned staff and providers
   */
  const loadDepartmentDetails = useCallback(async (departmentId) => {
    try {
      console.log("🏢 Loading department details for:", departmentId);
      const result = await getDepartmentDetails(departmentId);

      if (result.success && result.data) {
        const dept = result.data;
        console.log("📋 Department details:", dept);

        // Store department assignment data in state
        const staffIds = dept.staffIds || [];
        const providerIds = dept.renderingProviderIds || [];

        console.log("👥 Department staff IDs from API:", staffIds);
        console.log("🏥 Department provider IDs from API:", providerIds);

        // Update department assignment state
        setDepartmentStaffIds(staffIds);
        setDepartmentProviderIds(providerIds);

        // Set selected IDs for UI
        setSelectedUserIds(staffIds);
        setSelectedProviderIds(providerIds);
      } else {
        // If department details API failed, clear selections
        console.warn("⚠️ Department details API failed");
        setDepartmentStaffIds([]);
        setDepartmentProviderIds([]);
        setSelectedUserIds([]);
        setSelectedProviderIds([]);
      }
    } catch (error) {
      console.warn("⚠️ Could not load department details:", error);
      // Clear selections on error
      setDepartmentStaffIds([]);
      setDepartmentProviderIds([]);
      setSelectedUserIds([]);
      setSelectedProviderIds([]);
    }
  }, []);

  /**
   * Loads all allocation data when component mounts
   * Only loads if not already loaded or if facility changes
   */
  const loadAllocationData = useCallback(async () => {
    if (!facilityData?._id) {
      console.warn("No facility ID available for loading allocation data");
      return;
    }

    // Skip loading if we already have facility data for this facility
    if (facilityDataLoaded) {
      console.log("📊 Facility data already loaded, skipping reload");
      // Just set department ID if it's in URL - useEffect will handle loading details
      const departmentId = searchParams.get("departmentId");
      if (departmentId) {
        console.log("🎯 Setting department ID from URL param:", departmentId);
        setSelectedDepartmentId(departmentId);
      }
      return;
    }

    try {
      console.log(
        "🚀 Starting to load all allocation data for facility:",
        facilityData._id
      );

      // Start by loading departments
      setDepartmentsLoading(true);
      const departmentId = searchParams.get("departmentId");

      const result = await DepartmentAllocationService.getAllAllocationData(
        facilityData._id,
        departmentId
      );

      if (result.success) {
        const {
          departments: depts,
          users: usrs,
          providers: provs,
          selectedDepartmentId: selectedDept,
        } = result.data;

        // Debug logging for providers
        console.log("📊 Received providers data:", provs);
        if (provs && provs.length > 0) {
          console.log("📋 First provider sample:", {
            fullName: provs[0]?.fullName,
            npi: provs[0]?.npi,
            specialties: provs[0]?.specialties,
            _id: provs[0]?._id,
          });
        }

        // Update state with loaded data
        setDepartments(depts || []);
        setUsers(usrs || []);
        setProviders(provs || []);
        setSelectedDepartmentId(selectedDept);

        // If we have departments and no departmentId in URL, set the first one
        const targetDeptId =
          departmentId || (depts && depts.length > 0 ? depts[0]._id : null);
        if (targetDeptId) {
          setSelectedDepartmentId(targetDeptId);
          if (!departmentId) {
            updateDepartmentIdInURL(targetDeptId);
          }

          // Load department details directly to avoid duplicate calls
          console.log(
            "🎯 Loading initial department details for:",
            targetDeptId
          );
          await loadDepartmentDetails(targetDeptId);
        }

        console.log("✅ Successfully loaded all allocation data:", {
          departments: depts?.length || 0,
          users: usrs?.length || 0,
          providers: provs?.length || 0,
          selectedDept,
        });

        // Mark facility data as loaded and initial load complete
        setFacilityDataLoaded(true);
        setInitialLoadComplete(true);
        setInitialLoading(false);
      } else {
        console.error("❌ Failed to load allocation data:", result.message);
        message.error(result.message || "Failed to load allocation data");
      }
    } catch (error) {
      console.error("❌ Error loading allocation data:", error);
      message.error("Failed to load allocation data. Please try again.");
    } finally {
      setDepartmentsLoading(false);
      setUsersLoading(false);
      setProvidersLoading(false);
      setInitialLoading(false);
    }
  }, [
    facilityData?._id,
    searchParams,
    updateDepartmentIdInURL,
    facilityDataLoaded,
    loadDepartmentDetails,
  ]);

  // Reset facility data loaded flag when facility changes
  useEffect(() => {
    console.log("🔄 Facility changed, resetting facility data loaded flag");
    setFacilityDataLoaded(false);
    setInitialLoadComplete(false);
    setInitialLoading(true);
  }, [facilityData?._id]);

  // Load department details when selectedDepartmentId changes (but only after initial load)
  useEffect(() => {
    if (selectedDepartmentId && facilityDataLoaded && initialLoadComplete) {
      console.log(
        "🎯 Loading department details for department change:",
        selectedDepartmentId
      );
      loadDepartmentDetails(selectedDepartmentId);
    }
  }, [
    selectedDepartmentId,
    facilityDataLoaded,
    initialLoadComplete,
    loadDepartmentDetails,
  ]);

  /**
   * Check if a user is floating (no department assignments and is a user role)
   */
  const isFloatingUser = (user) => {
    // Must have 'user' role
    const hasUserRole =
      user.role &&
      Array.isArray(user.role) &&
      user.role.some((role) => role.toLowerCase() === "user");
    if (!hasUserRole) return false;

    // Check if departmentIds is null, undefined, or empty array
    const hasNoDepartments =
      !user.departmentIds ||
      user.departmentIds === null ||
      (Array.isArray(user.departmentIds) && user.departmentIds.length === 0);

    return hasNoDepartments;
  };

  /**
   * Check if a provider is floating (has facility affiliation with null departmentId)
   * A provider is floating if ALL their affiliations with the current facility have null departmentId
   */
  const isFloatingProvider = (provider) => {
    if (!provider.affiliations || !Array.isArray(provider.affiliations))
      return false;

    // Get all affiliations for the current facility
    const currentFacilityAffiliations = provider.affiliations.filter(
      (affiliation) => affiliation.facilityId === facilityData?._id
    );

    // If no affiliations with current facility, not floating
    if (currentFacilityAffiliations.length === 0) return false;

    // Provider is floating only if ALL affiliations with current facility have null departmentId
    return currentFacilityAffiliations.every(
      (affiliation) => affiliation.departmentId === null
    );
  };

  /**
   * Handles department selection change
   */
  const handleDepartmentChange = useCallback(
    (departmentId) => {
      console.log("🏢 Department changed to:", departmentId);
      setSelectedDepartmentId(departmentId);
      updateDepartmentIdInURL(departmentId);

      // Try to load department details first, with fallback to existing logic
      loadDepartmentDetails(departmentId);
    },
    [updateDepartmentIdInURL, loadDepartmentDetails]
  );

  /**
   * Filters and sorts users based on search query (only includes members)
   * Excludes admin, supervisor, and other roles - only shows "user" role
   * Can optionally show only floating users (no department assignments)
   * Sorting: Available users first, currently assigned users last
   */
  const filteredUsers = users
    .filter((user) => {
      // Only include users with 'user' role (exclude admin, supervisor, etc.)
      if (user.role && Array.isArray(user.role)) {
        return user.role.some((role) => role.toLowerCase() === "user");
      }
      return false; // If no role array, exclude the user
    })
    .filter((user) => {
      // If showing only floating members, filter by floating status
      if (showOnlyFloatingMembers) {
        return isFloatingUser(user);
      }
      return true;
    })
    .filter((user) => {
      if (!userSearchQuery) return true;
      const query = userSearchQuery.toLowerCase();
      return (
        user.name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query) ||
        user.role?.some((role) => role.toLowerCase().includes(query))
      );
    })
    .sort((a, b) => {
      // Check if users are currently in the selected department (using department data)
      const aIsInDept = departmentStaffIds.includes(a._id);
      const bIsInDept = departmentStaffIds.includes(b._id);

      // Sort: available users (false) first, currently assigned (true) last
      if (aIsInDept && !bIsInDept) return 1; // a goes after b
      if (!aIsInDept && bIsInDept) return -1; // a goes before b

      // If both have same assignment status, sort alphabetically by name
      return (a.name || "").localeCompare(b.name || "");
    });

  /**
   * Filters and sorts providers based on search query
   * Can optionally show only floating providers (facility affiliations with null departmentId)
   * Only shows providers who have affiliations with the current facility
   * Sorting: Available providers first, currently assigned providers last
   */
  const filteredProviders = providers
    .filter((provider) => {
      // Only show providers who have affiliations with the current facility
      const hasCurrentFacilityAffiliation = provider.affiliations?.some(
        (affiliation) => affiliation.facilityId === facilityData?._id
      );
      if (!hasCurrentFacilityAffiliation) {
        return false; // Exclude providers not affiliated with current facility
      }
      
      // If showing only floating members, filter by floating status
      if (showOnlyFloatingMembers) {
        return isFloatingProvider(provider);
      }
      return true;
    })
    .filter((provider) => {
      if (!providerSearchQuery) return true;
      const query = providerSearchQuery.toLowerCase();
      return (
        provider.fullName?.toLowerCase().includes(query) ||
        provider.npi?.toLowerCase().includes(query) ||
        provider.specialties?.some((specialty) =>
          specialty.toLowerCase().includes(query)
        )
      );
    })
    .sort((a, b) => {
      // Check if providers are currently in the selected department (using department data)
      const aIsInDept = departmentProviderIds.includes(a._id);
      const bIsInDept = departmentProviderIds.includes(b._id);

      // Sort: available providers (false) first, currently assigned (true) last
      if (aIsInDept && !bIsInDept) return 1; // a goes after b
      if (!aIsInDept && bIsInDept) return -1; // a goes before b

      // If both have same assignment status, sort alphabetically by name
      return (a.fullName || "").localeCompare(b.fullName || "");
    });

  /**
   * Calculate new user selections (not counting existing department members)
   */
  const getNewUserSelections = () => {
    if (!selectedDepartmentId) return [];
    // Use department data instead of user.departmentIds
    return selectedUserIds.filter((id) => !departmentStaffIds.includes(id));
  };

  /**
   * Calculate new provider selections (not counting existing department members)
   */
  const getNewProviderSelections = () => {
    if (!selectedDepartmentId) return [];
    // Use department data instead of provider.affiliations
    return selectedProviderIds.filter(
      (id) => !departmentProviderIds.includes(id)
    );
  };

  const hasNewUserSelections = getNewUserSelections().length > 0;
  const hasNewProviderSelections = getNewProviderSelections().length > 0;

  // Calculate floating member counts
  const floatingUserCount = users.filter((user) => {
    const hasUserRole =
      user.role &&
      Array.isArray(user.role) &&
      user.role.some((role) => role.toLowerCase() === "user");
    return hasUserRole && isFloatingUser(user);
  }).length;

  const floatingProviderCount = providers.filter((provider) => {
    // Only count providers affiliated with current facility
    const hasCurrentFacilityAffiliation = provider.affiliations?.some(
      (affiliation) => affiliation.facilityId === facilityData?._id
    );
    return hasCurrentFacilityAffiliation && isFloatingProvider(provider);
  }).length;

  /**
   * Handles user checkbox change
   */
  const handleUserCheckboxChange = (userId, checked) => {
    setSelectedUserIds((prev) => {
      if (checked) {
        return [...prev, userId];
      } else {
        return prev.filter((id) => id !== userId);
      }
    });
  };

  /**
   * Handles provider checkbox change
   */
  const handleProviderCheckboxChange = (providerId, checked) => {
    setSelectedProviderIds((prev) => {
      if (checked) {
        return [...prev, providerId];
      } else {
        return prev.filter((id) => id !== providerId);
      }
    });
  };

  /**
   * Handles user allocation submission
   */
  const handleUserSubmit = async () => {
    if (!selectedDepartmentId) {
      message.error("Please select a department first");
      return;
    }

    if (!hasNewUserSelections) {
      message.error("Please select at least one new user");
      return;
    }

    try {
      // Set loading state
      setSavingUsers(true);

      console.log("🎯 User Allocation Data:", {
        departmentId: selectedDepartmentId,
        allSelectedUserIds: selectedUserIds,
        newUserIds: getNewUserSelections(),
        facilityId: facilityData?._id,
      });

      // Call API to save staff allocation
      const result = await saveStaffAllocation(
        selectedDepartmentId,
        selectedUserIds
      );

      if (result.success) {
        message.success(
          `Successfully saved ${
            getNewUserSelections().length
          } new user(s) to department`
        );

        // Refresh only the department data to reflect changes - much more efficient!
        await loadDepartmentDetails(selectedDepartmentId);
      } else {
        message.error(result.message || "Failed to save user allocation");
      }
    } catch (error) {
      console.error("❌ Error saving user allocation:", error);
      message.error("Failed to save user allocation. Please try again.");
    } finally {
      // Always reset loading state
      setSavingUsers(false);
    }
  };

  /**
   * Extract role from provider's affiliation with current facility
   */
  const getProviderRoleForFacility = (provider) => {
    if (!provider.affiliations || !Array.isArray(provider.affiliations)) {
      // If no affiliations at all, provide a default role
      return ["Provider"]; // Default role instead of empty array
    }

    // Find affiliation with current facility
    const facilityAffiliation = provider.affiliations.find(
      (affiliation) => affiliation.facilityId === facilityData?._id
    );

    if (facilityAffiliation?.role && Array.isArray(facilityAffiliation.role) && facilityAffiliation.role.length > 0) {
      return facilityAffiliation.role;
    }

    // If no role found for current facility, provide a default
    return ["Provider"]; // Default role instead of empty array
  };

  /**
   * Transform selected provider IDs into the new API format with roles
   */
  const transformProvidersForAPI = (providerIds) => {
    return providerIds.map((providerId) => {
      const provider = providers.find((p) => p._id === providerId);
      const role = getProviderRoleForFacility(provider);

      return {
        id: providerId,
        role: role,
      };
    });
  };

  /**
   * Handles provider allocation submission
   */
  const handleProviderSubmit = async () => {
    if (!selectedDepartmentId) {
      message.error("Please select a department first");
      return;
    }

    if (!hasNewProviderSelections) {
      message.error("Please select at least one new provider");
      return;
    }

    try {
      // Set loading state
      setSavingProviders(true);

      // Transform provider IDs to new API format with roles
      const renderingProviderIds =
        transformProvidersForAPI(selectedProviderIds);
      const newProviderIds = getNewProviderSelections();
      const newRenderingProviders = transformProvidersForAPI(newProviderIds);

      console.log("🎯 Provider Allocation Data:", {
        departmentId: selectedDepartmentId,
        allSelectedProviderIds: selectedProviderIds,
        newProviderIds: newProviderIds,
        renderingProviderIds: renderingProviderIds,
        newRenderingProviders: newRenderingProviders,
        facilityId: facilityData?._id,
      });

      // Call API to save provider allocation with new format
      const result = await saveProviderAllocation(
        selectedDepartmentId,
        renderingProviderIds
      );

      if (result.success) {
        message.success(
          `Successfully saved ${newProviderIds.length} new provider(s) to department`
        );

        // Refresh only the department data to reflect changes - much more efficient!
        await loadDepartmentDetails(selectedDepartmentId);
      } else {
        message.error(result.message || "Failed to save provider allocation");
      }
    } catch (error) {
      console.error("❌ Error saving provider allocation:", error);
      message.error("Failed to save provider allocation. Please try again.");
    } finally {
      // Always reset loading state
      setSavingProviders(false);
    }
  };

  // Load initial data when component mounts or facility changes
  useEffect(() => {
    if (facilityData?._id) {
      loadAllocationData();
    }
  }, [facilityData?._id, loadAllocationData]);

  // Handle URL parameter changes for department selection
  useEffect(() => {
    const urlDepartmentId = searchParams.get("departmentId");
    if (urlDepartmentId && urlDepartmentId !== selectedDepartmentId) {
      setSelectedDepartmentId(urlDepartmentId);
    }
  }, [searchParams, selectedDepartmentId]);

  // Initial loading screen for first page load
  if (initialLoading) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <div style={{ 
            width: '80px', 
            height: '80px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <LogoLoader />
          </div>
          <Text style={{
            fontSize: '0.875rem',
            color: themeMode === 'dark' ? '#ffffff' : '#666666',
            textAlign: 'center'
          }}>
            Loading department allocation data...
          </Text>
        </div>
      );
  }

  // Skeleton loader for when facility data is loading
  if (facilityLoading) {
    return (
      <div style={{ padding: "24px" }}>
        <Skeleton active paragraph={{ rows: 2 }} />
        <br />
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: 1800,
        mx: "auto",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        "& > *": {
          width: "100%",
          overflow: "auto",
          "&::-webkit-scrollbar": {
            width: "0px",
            height: "8px",
            background: "transparent",
          },
          "&::-webkit-scrollbar-track": {
            background: themeMode === "dark" ? "#2d2d2d" : "#f1f1f1",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: themeMode === "dark" ? "#555" : "#888",
            borderRadius: "4px",
            "&:hover": {
              background: themeMode === "dark" ? "#666" : "#999",
            },
          },
          "&:not(:hover)::-webkit-scrollbar-thumb": {
            background: "transparent",
          },
        },
      }}
    >
      {/* Header Section */}
      <div
        style={{
          marginBottom: "0.5rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: "0.5rem",
        }}
      >
        {/* Title Section */}
        <div>
          <Title
            level={3}
            style={{
              margin: "0 0 0.25rem 0",
              color: themeMode === "dark" ? "#ffffff" : "#000000",
            }}
          >
            Department Allocation
          </Title>

          <Text
            style={{
              fontSize: "0.875rem",
              color: themeMode === "dark" ? "#ffffff" : "#000000",
              display: "block",
              marginBottom: "0.125rem",
            }}
          >
            Assign users and providers to departments in {facilityName} (
            <Text
              style={{
                fontSize: "0.8125rem",
                color: themeMode === "dark" ? "#a0a0a0" : "#666666",
              }}
            >
              {facilityAddress}
            </Text>
            )
          </Text>

          {/* <Text 
            style={{
              fontSize: '0.8125rem',
              color: themeMode === 'dark' ? '#a0a0a0' : '#666666'
            }}
          >
            {facilityAddress}
          </Text> */}
        </div>

        {/* Header Action Buttons */}
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <Button
            variant="outlined"
            onClick={onClose}
            label="Back to Facility List"
            style={{ borderRadius: "6px" }}
            backgroundColor={currentTheme.loginPage.textBoxBackground}
            color={currentTheme.text.primary}
          />
          <ThemedTooltip
            title="Users and providers who need to be assigned to a department in this facility"
            placement="bottom"
            arrow={true}
          >
            <div>
              <Button
                variant={showOnlyFloatingMembers ? "contained" : "outlined"}
                onClick={() =>
                  setShowOnlyFloatingMembers(!showOnlyFloatingMembers)
                }
                label={`${
                  showOnlyFloatingMembers ? "Show All" : "Show Floating"
                } Members ${
                  floatingUserCount + floatingProviderCount > 0
                    ? `(${floatingUserCount + floatingProviderCount})`
                    : ""
                }`}
                style={{
                  borderRadius: "6px",
                  fontSize: "0.8125rem",
                }}
              />
            </div>
          </ThemedTooltip>
        </div>
      </div>

      {/* <Divider /> */}

      {/* Main Content Area */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(25rem, 1fr))",
          gap: "1rem",
          marginBottom: "1rem",
        }}
      >
        {/* Departments Section */}
        <Card
          title={
            <span
              style={{ color: themeMode === "dark" ? "#ffffff" : "#000000" }}
            >
              Departments ({departments.length})
            </span>
          }
          bordered={false}
          style={{
            backgroundColor: themeMode === "dark" ? "#04002b" : "#ffffff",
            borderRadius: "0.5rem",
            border: `1px solid ${themeMode === "dark" ? "#302d4c" : "#e9e8ea"}`,
            maxHeight: "69vh",
            overflowY: "auto",
          }}
        >
          {departmentsLoading ? (
            <div style={{ padding: "1.25rem 0" }}>
              <Skeleton active paragraph={{ rows: 3 }} />
            </div>
          ) : departments && departments.length > 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
              }}
            >
              {departments.map((dept, index) => (
                <Box
                  key={dept._id || index}
                  onClick={() => handleDepartmentChange(dept._id)}
                  sx={{
                    backgroundColor:
                      selectedDepartmentId === dept._id
                        ? themeMode === "dark"
                          ? "rgba(230, 62, 43, 0.2)"
                          : "rgba(230, 62, 43, 0.1)"
                        : themeMode === "dark"
                        ? "#302d4c"
                        : "#f8f9fa",
                    border: `0.125rem solid ${
                      selectedDepartmentId === dept._id
                        ? brandOrange
                        : "transparent"
                    }`,
                    borderRadius: "0.5rem",
                    padding: "1rem",
                    cursor: "pointer",
                    transition:
                      "transform 0.2s ease, background-color 0.2s ease, border-color 0.2s ease",
                    transform: "scale(1)",
                    "&:hover": {
                      transform: "scale(0.9)",
                    },
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <Text
                        strong
                        style={{
                          fontSize: "0.875rem",
                          color:
                            selectedDepartmentId === dept._id
                              ? brandOrange
                              : themeMode === "dark"
                              ? "#ffffff"
                              : "#000000",
                        }}
                      >
                        {dept.name ||
                          dept.departmentName ||
                          `Department ${index + 1}`}
                      </Text>
                      {dept.faxNumber && (
                        <div>
                          <Text
                            style={{
                              fontSize: "0.75rem",
                              color:
                                themeMode === "dark" ? "#a0a0a0" : "#666666",
                            }}
                          >
                            Fax: {dept.faxNumber}
                          </Text>
                        </div>
                      )}
                      {selectedDepartmentId === dept._id && (
                        <div>
                          <Text
                            style={{
                              fontSize: "0.75rem",
                              color: brandOrange,
                              fontWeight: "bold",
                            }}
                          >
                            ✓ Currently Selected
                          </Text>
                        </div>
                      )}
                    </div>
                  </div>
                </Box>
              ))}
            </div>
          ) : (
            <Empty
              description="No departments found for this facility"
              style={{ margin: "1.25rem 0" }}
            />
          )}
        </Card>

        {/* Users Section */}
        <Card
          title={
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                style={{ color: themeMode === "dark" ? "#ffffff" : "#000000" }}
              >
                Facility Users ({filteredUsers.length})
              </span>
              <div
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                {!selectedDepartmentId && (
                  <Tooltip
                    title="Select a department first to assign users"
                    placement="top"
                    color={themeMode === "dark" ? "#404040" : "#f5f5f5"}
                    overlayInnerStyle={{
                      color: themeMode === "dark" ? "#ffffff" : "#000000",
                      fontSize: "0.75rem",
                    }}
                  >
                    <ExclamationCircleOutlined
                      style={{
                        color: "#faad14",
                        fontSize: "1rem",
                        cursor: "help",
                      }}
                    />
                  </Tooltip>
                )}
                <Button
                  variant="contained"
                  onClick={handleUserSubmit}
                  disabled={
                    !selectedDepartmentId ||
                    !hasNewUserSelections ||
                    savingUsers
                  }
                  label={savingUsers ? "Saving..." : "Save Allocation"}
                  style={{
                    borderRadius: "6px",
                  }}
                />
              </div>
            </div>
          }
          bordered={false}
          style={{
            backgroundColor: themeMode === "dark" ? "#04002b" : "#ffffff",
            borderRadius: "0.5rem",
            border: `1px solid ${themeMode === "dark" ? "#302d4c" : "#e9e8ea"}`,
            height: CARD_HEIGHT,
          }}
        >
          {/* Search Input */}
          <div style={{ marginBottom: "1rem" }}>
            <Search
              value={userSearchQuery}
              onChange={setUserSearchQuery}
              themeMode={themeMode}
              currentTheme={currentTheme}
              style={{ width: "90%", marginBottom: "0.5rem" }}
              placeholder="Search users by name, email, or role..."
              fontSize="0.875rem"
            />
          </div>

          {usersLoading ? (
            <div style={{ padding: "1.25rem 0" }}>
              <Skeleton active paragraph={{ rows: 4 }} />
            </div>
          ) : filteredUsers && filteredUsers.length > 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
                maxHeight: LIST_MAX_HEIGHT,
                overflowY: "auto",
              }}
            >
              {filteredUsers.map((user, index) => {
                const isSelected = selectedUserIds.includes(user._id);
                const isCurrentlyInDept = departmentStaffIds.includes(user._id);

                return (
                  <div
                    key={user._id || user.id || index}
                    style={{
                      backgroundColor:
                        themeMode === "dark" ? "#302d4c" : "#f8f9fa",
                      border: `0.0625rem solid ${
                        isSelected
                          ? brandOrange
                          : themeMode === "dark"
                          ? "#404040"
                          : "#e9e8ea"
                      }`,
                      borderRadius: "0.375rem",
                      padding: "0.75rem",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "0.25rem",
                          }}
                        >
                          <Text
                            strong
                            style={{
                              fontSize: "0.8125rem",
                              color:
                                themeMode === "dark" ? "#ffffff" : "#000000",
                            }}
                          >
                            {user.name || "Unknown User"}
                          </Text>
                          {isCurrentlyInDept && (
                            <Text
                              style={{
                                fontSize: "0.6875rem",
                                color: "#10b981",
                                fontWeight: "bold",
                                marginLeft: "0.5rem",
                              }}
                            >
                              (Currently in department)
                            </Text>
                          )}
                        </div>
                        {user.email && (
                          <div>
                            <Text
                              style={{
                                fontSize: "0.6875rem",
                                color:
                                  themeMode === "dark" ? "#a0a0a0" : "#666666",
                              }}
                            >
                              {user.email}
                            </Text>
                          </div>
                        )}
                        {user.role && Array.isArray(user.role) && (
                          <div>
                            <Text
                              style={{
                                fontSize: "0.6875rem",
                                color:
                                  themeMode === "dark" ? "#a0a0a0" : "#666666",
                              }}
                            >
                              Role: {user.role.join(", ")}
                            </Text>
                          </div>
                        )}
                      </div>
                      <Checkbox
                        size="medium"
                        checked={isSelected || isCurrentlyInDept}
                        disabled={!selectedDepartmentId || isCurrentlyInDept}
                        onChange={(e) =>
                          handleUserCheckboxChange(user._id, e.target.checked)
                        }
                        inputProps={{ "aria-label": "select user" }}
                        sx={{
                          color: brandOrange,
                          "&.Mui-checked": { color: brandOrange },
                          "&.MuiCheckbox-indeterminate": { color: brandOrange },
                          "& .MuiSvgIcon-root": { fontSize: 20 },
                          "&.Mui-disabled": {
                            color: "#bdbdbd",
                            opacity: 0.6,
                            "& .MuiSvgIcon-root": {
                              color: "#bdbdbd",
                            },
                          },
                          "&.Mui-disabled.Mui-checked": {
                            color: "#9e9e9e",
                            opacity: 0.7,
                            "& .MuiSvgIcon-root": {
                              color: "#9e9e9e",
                            },
                          },
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : users.length > 0 ? (
            <Empty
              description={
                userSearchQuery
                  ? "No users found matching your search"
                  : "No users found for this facility"
              }
              style={{ margin: "1.25rem 0" }}
            />
          ) : (
            <Empty
              description="No users found for this facility"
              style={{ margin: "1.25rem 0" }}
            />
          )}
        </Card>

        {/* Providers Section */}
        <Card
          title={
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                style={{ color: themeMode === "dark" ? "#ffffff" : "#000000" }}
              >
                Facility Providers ({filteredProviders.length})
              </span>
              <div
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                {!selectedDepartmentId && (
                  <Tooltip
                    title="Select a department first to assign providers"
                    placement="top"
                    color={themeMode === "dark" ? "#404040" : "#f5f5f5"}
                    overlayInnerStyle={{
                      color: themeMode === "dark" ? "#ffffff" : "#000000",
                      fontSize: "0.75rem",
                    }}
                  >
                    <ExclamationCircleOutlined
                      style={{
                        color: "#faad14",
                        fontSize: "1rem",
                        cursor: "help",
                      }}
                    />
                  </Tooltip>
                )}
                <Button
                  variant="contained"
                  onClick={handleProviderSubmit}
                  disabled={
                    !selectedDepartmentId ||
                    !hasNewProviderSelections ||
                    savingProviders
                  }
                  label={savingProviders ? "Saving..." : "Save Allocation"}
                  style={{ borderRadius: "6px" }}
                />
              </div>
            </div>
          }
          bordered={false}
          style={{
            backgroundColor: themeMode === "dark" ? "#04002b" : "#ffffff",
            borderRadius: "0.5rem",
            border: `1px solid ${themeMode === "dark" ? "#302d4c" : "#e9e8ea"}`,
            height: CARD_HEIGHT,
          }}
        >
          {/* Search Input */}
          <div style={{ marginBottom: "1rem" }}>
            <Search
              value={providerSearchQuery}
              onChange={setProviderSearchQuery}
              themeMode={themeMode}
              currentTheme={currentTheme}
              style={{ width: "90%", marginBottom: "0.5rem" }}
              placeholder="Search providers by name, NPI, or specialty..."
              fontSize="0.875rem"
            />
          </div>

          {providersLoading ? (
            <div style={{ padding: "1.25rem 0" }}>
              <Skeleton active paragraph={{ rows: 4 }} />
            </div>
          ) : filteredProviders && filteredProviders.length > 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
                maxHeight: LIST_MAX_HEIGHT,
                overflowY: "auto",
              }}
            >
              {filteredProviders.map((provider, index) => {
                const isSelected = selectedProviderIds.includes(provider._id);
                const isCurrentlyInDept = departmentProviderIds.includes(
                  provider._id
                );

                return (
                  <div
                    key={provider._id || provider.id || `provider-${index}`}
                    style={{
                      backgroundColor:
                        themeMode === "dark" ? "#302d4c" : "#f8f9fa",
                      border: `0.0625rem solid ${
                        isSelected
                          ? brandOrange
                          : themeMode === "dark"
                          ? "#404040"
                          : "#e9e8ea"
                      }`,
                      borderRadius: "0.375rem",
                      padding: "0.75rem",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "0.25rem",
                          }}
                        >
                          <Text
                            strong
                            style={{
                              fontSize: "0.8125rem",
                              color:
                                themeMode === "dark" ? "#ffffff" : "#000000",
                            }}
                          >
                            {provider?.fullName || `Provider ${index + 1}`}
                          </Text>
                          {isCurrentlyInDept && (
                            <Text
                              style={{
                                fontSize: "0.6875rem",
                                color: "#10b981",
                                fontWeight: "bold",
                                marginLeft: "0.5rem",
                              }}
                            >
                              (Currently in department)
                            </Text>
                          )}
                        </div>
                        {provider.npi && (
                          <div>
                            <Text
                              style={{
                                fontSize: "0.6875rem",
                                color:
                                  themeMode === "dark" ? "#a0a0a0" : "#666666",
                              }}
                            >
                              NPI: {provider.npi}
                            </Text>
                          </div>
                        )}
                        {provider.specialties &&
                          Array.isArray(provider.specialties) &&
                          provider.specialties.length > 0 && (
                            <div>
                              <Text
                                style={{
                                  fontSize: "0.6875rem",
                                  color:
                                    themeMode === "dark"
                                      ? "#a0a0a0"
                                      : "#666666",
                                }}
                              >
                                Specialties:{" "}
                                {provider.specialties.slice(0, 2).join(", ")}
                                {provider.specialties.length > 2 &&
                                  ` +${provider.specialties.length - 2} more`}
                              </Text>
                            </div>
                          )}
                      </div>
                      <Checkbox
                        size="medium"
                        checked={isSelected || isCurrentlyInDept}
                        disabled={!selectedDepartmentId || isCurrentlyInDept}
                        onChange={(e) =>
                          handleProviderCheckboxChange(
                            provider._id,
                            e.target.checked
                          )
                        }
                        inputProps={{ "aria-label": "select provider" }}
                        sx={{
                          color: brandOrange,
                          "&.Mui-checked": { color: brandOrange },
                          "&.MuiCheckbox-indeterminate": { color: brandOrange },
                          "& .MuiSvgIcon-root": { fontSize: 20 },
                          "&.Mui-disabled": {
                            color: "#bdbdbd",
                            opacity: 0.6,
                            "& .MuiSvgIcon-root": {
                              color: "#bdbdbd",
                            },
                          },
                          "&.Mui-disabled.Mui-checked": {
                            color: "#9e9e9e",
                            opacity: 0.7,
                            "& .MuiSvgIcon-root": {
                              color: "#9e9e9e",
                            },
                          },
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : providers.length > 0 ? (
            <Empty
              description={
                providerSearchQuery
                  ? "No providers found matching your search"
                  : "No providers found for this facility"
              }
              style={{ margin: "1.25rem 0" }}
            />
          ) : (
            <Empty
              description="No providers found for this facility"
              style={{ margin: "1.25rem 0" }}
            />
          )}
        </Card>
      </div>
    </Box>
  );
};

export default DepartmentAllocation;
